import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Popconfirm
} from 'antd'

import {
  useTreatmentServiceGetAccessRequestsListEndpointKey,
  useTreatmentServiceRejectRequest
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  id: string
  onCancel: () => void
} & ModalProps

export const RefuseAccessRequestModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { mutate, isPending } = useTreatmentServiceRejectRequest({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      message.success(t('epidemiology.success_refuse_request'))
      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetAccessRequestsListEndpointKey]
      })
      onCancel()
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({ id, requestBody: values })
  }

  return (
    <Modal
      open
      centered
      title={t('epidemiology.refuse_reason')}
      width={520}
      maskClosable={false}
      footer={null}
      onCancel={onCancel}
      {...props}
    >
      <Form form={form} requiredMark={false} layout="vertical">
        <Form.Item
          name="reason"
          label={t('epidemiology.refuse_reason')}
          rules={requiredRules}
        >
          <Input.TextArea
            className={styles.caseTextarea}
            placeholder={t('epidemiology.refuse_reason')}
          />
        </Form.Item>
      </Form>

      <Divider />

      <Flex justify="center" gap={8}>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>

        <Popconfirm
          onConfirm={onSubmit}
          okButtonProps={{ loading: isPending }}
          title={t('epidemiology.refuse_access_request')}
        >
          <Button type="primary">{t('persons.save')}</Button>
        </Popconfirm>
      </Flex>
    </Modal>
  )
}
